import React from "react";
import {observer} from "mobx-react";
import {storesContext} from "../../../../common/stores/stores";
import {ITimetableStore} from "../../../stores/timetableStore";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import i18n from "../../../../i18n/i18n";
import {TimetableActionTypes} from "../../../models/timetable";
import ArrivalActionSection from "../arrivalActionSection/arrivalActionSection";
import StopActionSection from "../stopActionSection/stopActionSection";
import DepartureActionSection from "../departureActionSection/departureActionSection";
import "./formSection.scss";
import Button from "../../../../common/components/shared/button/button";
import {DropDownModel} from "../../../../common/models/dropDown";
import {DropDownUtils} from "../../../../helpers/dropDownUtils";
import {WharfsModel} from "../../../../topology/models/wharf";
import {WharfService} from "../../../../topology/services/wharfService";
import {FerryService} from "../../../../dictionaries/services/ferryService";
import {TimetableService} from "../../../services/timetableService";
import {RouteComponentProps, withRouter} from "react-router";
import {PortService} from "../../../../dictionaries/services/portService";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {Api} from "../../../../helpers/api";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {RouteService} from "../../../../topology/services/routeService";
import Loader from "../../../../common/components/shared/loader/loader";

interface FormSectionState {
    wharfs: WharfsModel;
    getActionResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isLoading: boolean;
    wharfsResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    ferriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    portsResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    routesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

interface FormSectionProps extends RouteComponentProps {
    isUpdate: boolean;
    actionId?: string;
    actionIndex: number;
    isVisibleRemoveButton: boolean;
    onRemoveAction: () => void;
    isVisibleCancelButton: boolean;
    onCancelAction: () => void;
}

class FormSection extends React.Component <FormSectionProps, FormSectionState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            wharfs: {wharfs: []},
            getActionResponse: undefined,
            isLoading: false,
            wharfsResponse: undefined,
            ferriesResponse: undefined,
            portsResponse: undefined,
            routesResponse: undefined
        };
    }

    get wharfs() {
        return this.state.wharfs;
    }

    async componentDidMount() {
        this.setState({isLoading: true});
        await this.getWharfs();
        await this.getFerries();
        await this.getPorts();
        await this.getRoutes();

        if (this.props.isUpdate && this.props.actionId) {
            await this.getAction();
        }
        this.setState({isLoading: false});
    }

    render() {
        const timetableStore = this.context.timetableStore as ITimetableStore;

        if (this.state.isLoading) {
            return <Loader />;
        } else {
            return (
                <div className="form-section">
                    {this.props.isVisibleCancelButton && (
                        <Button
                            text={i18n.t("cancel")}
                            type="primary"
                            variant="text"
                            className="cancel-action-btn"
                            testId={`cancel-${this.props.actionIndex}`}
                            onClick={() => this.props.onCancelAction()}
                        />
                    )}
                    {this.props.isVisibleRemoveButton && (
                        <Button
                            text={i18n.t("remove")}
                            type="primary"
                            variant="text"
                            className="remove-action-btn"
                            testId={`remove-${this.props.actionIndex}`}
                            onClick={() => this.props.onRemoveAction()}
                        />
                    )}
                    <div className="section">
                        <DropDown
                            label={i18n.t("timetable.headers.ferry")}
                            validatedValue={timetableStore.actions[this.props.actionIndex].ferryId}
                            items={this.ferriesToDropDownMap()}
                            testId={`ferry-${this.props.actionIndex}`}
                            disabled={timetableStore.actions[this.props.actionIndex].hasOrderRegistered}
                            error={!Api.isSuccess(this.state.ferriesResponse) ? i18n.t("errorMessages.ferriesErrorSuffix") : ""}
                            reloadFunc={() => this.getFerries()}
                        />
                        <DropDown
                            label={i18n.t("timetable.headers.actionType")}
                            validatedValue={timetableStore.actions[this.props.actionIndex].actionType}
                            items={this.actionsToDropDownMap()}
                            testId={`action-type-${this.props.actionIndex}`}
                            disabled={this.props.isUpdate}
                            withoutSort
                        />

                    </div>

                    {this.renderActionSpecificSection(timetableStore)}
                    {!Api.isSuccess(this.state.getActionResponse) && (
                        <HttpError
                            response={this.state.getActionResponse}
                            cause={i18n.t("timetable.actions.errors.errorMessage")}
                        />
                    )}
                </div>
            );
        }
    }

    private renderActionSpecificSection(timetableStore: ITimetableStore) {
        switch (timetableStore.actions[this.props.actionIndex].actionType.value) {
            case TimetableActionTypes.ARRIVAL:
                return (<div className="arrival-section">
                    <ArrivalActionSection
                        actionIndex={this.props.actionIndex}
                        wharfs={this.wharfsToDropDownMap()}
                        whrafsResponse={this.state.wharfsResponse}
                        reloadFunc={() => this.getWharfs()}
                    />
                </div>);
            case TimetableActionTypes.STOP:
                return (<div className="stop-section">
                    <StopActionSection
                        actionIndex={this.props.actionIndex}
                        wharfs={this.wharfsToDropDownMap()}
                        whrafsResponse={this.state.wharfsResponse}
                        reloadFunc={() => this.getWharfs()}
                    />
                </div>);
            case TimetableActionTypes.DEPARTURE:
                return (<div className="departure-section">
                    <DepartureActionSection
                        actionIndex={this.props.actionIndex}
                        wharfs={this.wharfsToDropDownMap()}
                        whrafsResponse={this.state.wharfsResponse}
                        whrafsReload={() => this.getWharfs()}
                        portsResponse={this.state.portsResponse}
                        portsReload={() => this.getPorts()}
                        routesResponse={this.state.routesResponse}
                        routesReload={() => this.getRoutes()}
                    />
                </div>);
            default:
                return (<></>);
        }
    }

    private actionsToDropDownMap(): DropDownModel[] {
        return [
            {
                id: TimetableActionTypes.ARRIVAL,
                name: i18n.t("timetable.actions.names.arrival"),
            },
            {
                id: TimetableActionTypes.STOP,
                name: i18n.t("timetable.actions.names.stop"),
            },
            {
                id: TimetableActionTypes.DEPARTURE,
                name: i18n.t("timetable.actions.names.departure"),
            }
        ];
    }

    private async getWharfs() {
        const response = await WharfService.getAll();
        this.setState({wharfs: response?.data, wharfsResponse: response});
    }

    private wharfsToDropDownMap(): DropDownModel[] {
        if (!this.wharfs || !this.wharfs.wharfs || !this.wharfs.wharfs.length) {
            return [];
        }

        return DropDownUtils.listToDropDownMap(this.wharfs.wharfs);
    }

    private async getFerries() {
        const timetableStore = this.context.timetableStore as ITimetableStore;

        const response = await FerryService.getFerriesForDropDown();
        const ferries = response?.data || [];
        timetableStore.setFerries(ferries);

        this.setState({ferriesResponse: response});
    }

    private ferriesToDropDownMap(): DropDownModel[] {
        const timetableStore = this.context.timetableStore as ITimetableStore;
        const ferries = timetableStore.ferries;

        if (!ferries || !ferries.length) {
            return [];
        }

        return DropDownUtils.listToDropDownMap(ferries);
    }

    private async getPorts() {
        const timetableStore = this.context.timetableStore as ITimetableStore;

        const response = await PortService.getAll();
        const ports = response?.data?.data || [];
        timetableStore.setPorts(ports);

        this.setState({portsResponse: response});
    }

    private async getRoutes() {
        const timetableStore = this.context.timetableStore as ITimetableStore;

        const response = await RouteService.getRouteForDropDown();

        const routes = response?.data || [];

        timetableStore.setRoutes(routes.length > 1 ? routes.sort((a, b) => {
            return a.name.localeCompare(b.name);
        }) : routes);

        this.setState({routesResponse: response});
    }

    private async getAction() {
        const timetableStore = this.context.timetableStore as ITimetableStore;

        const response = await TimetableService.getAction(this.props.actionId!);

        this.setState({getActionResponse: response});
        timetableStore.setTimetableStore(response?.data || undefined);
    }
}

export default withRouter(observer(FormSection));
